import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { call, put, select, takeLatest } from 'redux-saga/effects';
import api from 'src/api/BlancoClient';
import { TipHistoryResponse } from 'src/api/response/lifeSupports';
import { PersistentData } from 'src/lib/PersistentData';
import { persistUserLifeSupportsTipHistoryState } from 'src/lib/storage';
import { TipHistory } from 'src/models/life-supports/TipHistory';
import { RootState } from '.';
import { actions as userActions, getFreshToken, UserState } from './user';
import { checkErrorType } from './utils/helper';
import { RequestState } from './utils/RequestState';

export type LifeSupportsErrorType = 'unstable-network' | 'unknown';

export interface LifeSupportsState {
  histories?: RequestState<TipHistory[], LifeSupportsErrorType>;
  previousReadTipSerialNum?: number; // 前回の徹底ライフサポートアプリ起動時で既読になったTipsの最新のシリアル番号
  userLifeSupportsTipHistoryStates?: PersistentData['userLifeSupportsTipHistoryStates'];
}

const initialState: LifeSupportsState = {};

const slice = createSlice({
  name: 'lifeSupports',
  initialState,
  reducers: {
    persistentDataRestored: (state, { payload }: PayloadAction<PersistentData>) => {
      state.userLifeSupportsTipHistoryStates = payload.userLifeSupportsTipHistoryStates;
    },
    getTipHistories: state => {
      state.histories = { state: 'pending' };
    },
    getTipHistoriesSucceeded: (
      state,
      {
        payload
      }: PayloadAction<{
        histories: TipHistory[];
        previousReadTipSerialNum?: number;
        nextUserLifeSupportsTipHistoryState?: PersistentData['userLifeSupportsTipHistoryStates'];
      }>
    ) => {
      state.histories = { state: 'ok', result: payload.histories };
      state.previousReadTipSerialNum = payload.previousReadTipSerialNum;
      state.userLifeSupportsTipHistoryStates = {
        ...state.userLifeSupportsTipHistoryStates,
        ...payload.nextUserLifeSupportsTipHistoryState
      };
    },
    getTipHistoriesFailed: (state, { payload }: PayloadAction<LifeSupportsErrorType>) => {
      state.histories = { state: 'error', error: payload };
    }
  }
});

export default slice.reducer;

const { persistentDataRestored, getTipHistories, ...internalActions } = slice.actions;

export const actions = { persistentDataRestored, getTipHistories };

const { getTipHistoriesSucceeded, getTipHistoriesFailed } = internalActions;

// Saga

export function* LifeSupportsSaga() {
  yield takeLatest(getTipHistories.type, getTipHistoriesSaga);
}

function* getTipHistoriesSaga() {
  try {
    const baid: string = yield call(getFreshToken);
    const userState: UserState = yield select((state: RootState) => state.user);
    const lifeSupportsState: LifeSupportsState = yield select(
      (state: RootState) => state.lifeSupports
    );
    const { selectedUserId: userId, language } = userState;
    const { userLifeSupportsTipHistoryStates } = lifeSupportsState;

    if (!userId || !language) {
      // 復帰不可能のエラー
      throw new Error();
    }

    const { tips: tipsRes }: { tips: TipHistoryResponse[]; language: string } = yield call(
      api.getTipHistories,
      baid,
      language
    );

    // 扱いやすいようにspeakerをunique IDから文字列リテラルに変換する
    const tips = tipsRes.map(tip => ({
      ...tip,
      speaker: getSpeakerName(tip.speaker)
    }));

    if (tips.length === 0) {
      // Tipsが0件の時は空の配列を返す
      yield put(
        getTipHistoriesSucceeded({
          histories: []
        })
      );
      return;
    }

    const userLifeSupportsTipHistoryState =
      userLifeSupportsTipHistoryStates && userLifeSupportsTipHistoryStates[userId];
    const previousReadTipSerialNum = userLifeSupportsTipHistoryState?.latestReadTipSerialNum;
    const latestReadTipSerialNum = tips[tips.length - 1].tipSerialNum;
    const nextUserLifeSupportsTipHistoryState = {
      [userId]: { latestReadTipSerialNum }
    };
    yield call(persistUserLifeSupportsTipHistoryState, userId, { latestReadTipSerialNum });
    yield put(
      getTipHistoriesSucceeded({
        histories: tips,
        previousReadTipSerialNum,
        nextUserLifeSupportsTipHistoryState
      })
    );
  } catch (error) {
    const errorType = checkErrorType(error);
    if (errorType === 'server') {
      yield put(userActions.causeServerError(error.response.data?.code));
    } else if (errorType === 'unstable-network') {
      yield put(getTipHistoriesFailed('unstable-network'));
    } else {
      yield put(getTipHistoriesFailed('unknown'));
    }
  }
}

// helper function
function getSpeakerName(speaker: TipHistoryResponse['speaker']) {
  switch (speaker) {
    case 34:
      return 'tanukichi' as const;
    case 38:
      return 'shizue' as const;
    case 56:
      return 'takumi' as const;
  }
}
